import React from 'react';
import FAQAccordion from '../components/ui/FAQAccordion';
import { SearchIcon, HelpCircleIcon, TruckIcon, CreditCardIcon, RefreshCwIcon } from 'lucide-react';
const FAQPage = () => {
  // FAQ categories with questions and answers
  const faqCategories = [{
    id: 'general',
    title: 'General Questions',
    icon: <HelpCircleIcon className="h-6 w-6" data-id="element-481" />,
    faqs: [{
      question: 'What is MarketHub?',
      answer: 'MarketHub is an online marketplace that connects buyers with trusted sellers offering unique, quality products. We provide a secure platform where you can discover a wide range of items from fashion to home decor, electronics, handmade crafts, and more.'
    }, {
      question: 'How do I create an account?',
      answer: "Creating an account is easy! Click the 'Sign Up' button in the top right corner, enter your email address, create a password, and follow the verification steps. You can also sign up using your Google or Facebook account for a quicker registration process."
    }, {
      question: 'Is it free to use MarketHub?',
      answer: 'Yes, browsing and shopping on MarketHub is completely free for customers. We only charge sellers a small commission on completed sales to maintain our platform and provide customer service.'
    }, {
      question: 'How do I contact customer support?',
      answer: "You can reach our customer support team through the 'Contact Us' page, by emailing support@markethub.com, or by using the live chat feature available at the bottom right of every page. Our support team is available Monday through Friday, 9 AM to 6 PM EST."
    }]
  }, {
    id: 'orders',
    title: 'Orders & Payments',
    icon: <CreditCardIcon className="h-6 w-6" data-id="element-482" />,
    faqs: [{
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover), PayPal, Apple Pay, and Google Pay. All payment information is securely processed and encrypted.'
    }, {
      question: 'How do I check the status of my order?',
      answer: "You can track your order status by logging into your account and visiting the 'My Orders' section. There you'll find all your orders with their current status, tracking information, and estimated delivery dates."
    }, {
      question: 'Can I change or cancel my order?',
      answer: 'You can request to change or cancel your order within 1 hour of placing it. After that, the order may have already been processed by the seller. Please contact customer support immediately if you need to make changes to a recent order.'
    }, {
      question: 'Is my payment information secure?',
      answer: 'Absolutely. We use industry-standard encryption and security measures to protect your payment information. We never store your full credit card details on our servers, and all transactions are processed through secure, PCI-compliant payment processors.'
    }, {
      question: 'When will my card be charged?',
      answer: "Your card will be charged immediately when you place your order. If for any reason your order cannot be fulfilled, we'll issue a full refund to your original payment method."
    }]
  }, {
    id: 'shipping',
    title: 'Shipping & Delivery',
    icon: <TruckIcon className="h-6 w-6" data-id="element-483" />,
    faqs: [{
      question: 'How long will it take to receive my order?',
      answer: "Shipping times vary depending on the seller's location, your location, and the shipping method selected. Most domestic orders arrive within 3-7 business days. International shipping typically takes 7-21 business days. You can find the estimated delivery time on each product page."
    }, {
      question: 'Do you ship internationally?',
      answer: "Yes, most of our sellers ship internationally. During checkout, you'll be able to see if the product can be shipped to your country and what the shipping costs will be. Please note that international orders may be subject to customs duties and taxes."
    }, {
      question: 'How much does shipping cost?',
      answer: 'Shipping costs vary by seller, product size/weight, destination, and shipping method. The exact shipping cost will be displayed during checkout before you complete your purchase. Many sellers offer free shipping on orders above a certain amount.'
    }, {
      question: 'Can I track my package?',
      answer: "Yes, once your order ships, you'll receive a shipping confirmation email with tracking information. You can also find tracking details in the 'My Orders' section of your account."
    }]
  }, {
    id: 'returns',
    title: 'Returns & Refunds',
    icon: <RefreshCwIcon className="h-6 w-6" data-id="element-484" />,
    faqs: [{
      question: 'What is your return policy?',
      answer: "Our marketplace has a standard 30-day return policy, though some sellers may have specific policies. If you're not satisfied with your purchase, you can initiate a return through your account within 30 days of receiving the item. The item must be unused and in its original packaging."
    }, {
      question: 'How do I return an item?',
      answer: "To return an item, go to 'My Orders' in your account, select the order containing the item you wish to return, and click 'Return Item'. Follow the instructions to print a return label and arrange for pickup or drop-off. Once the seller receives the returned item, your refund will be processed."
    }, {
      question: 'When will I receive my refund?',
      answer: 'Refunds are typically processed within 5-7 business days after the seller receives your returned item. The time it takes for the refund to appear in your account depends on your payment method and financial institution, usually 3-10 additional business days.'
    }, {
      question: 'What if I receive a damaged or incorrect item?',
      answer: "If you receive a damaged or incorrect item, please contact us within 48 hours of delivery. Take photos of the damaged item or packaging and submit them through the 'Report an Issue' option in your order details. We'll work with you and the seller to resolve the issue promptly."
    }]
  }];
  return <div className="bg-gray-50 min-h-screen" data-id="element-485">
      {/* Header */}
      <section className="bg-blue-600 text-white py-12" data-id="element-486">
        <div className="container mx-auto px-4 text-center" data-id="element-487">
          <h1 className="text-3xl font-bold mb-4" data-id="element-488">
            Frequently Asked Questions
          </h1>
          <p className="text-xl max-w-2xl mx-auto" data-id="element-489">
            Find answers to common questions about shopping, shipping, returns,
            and more.
          </p>
        </div>
      </section>
      {/* Search */}
      <section className="bg-white py-8 shadow-sm" data-id="element-490">
        <div className="container mx-auto px-4" data-id="element-491">
          <div className="max-w-2xl mx-auto relative" data-id="element-492">
            <input type="text" placeholder="Search for answers..." className="pl-12 pr-4 py-3 border border-gray-300 rounded-full text-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full" data-id="element-493" />
            <SearchIcon className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" data-id="element-494" />
          </div>
        </div>
      </section>
      {/* FAQ Content */}
      <section className="py-12" data-id="element-495">
        <div className="container mx-auto px-4" data-id="element-496">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-497">
            {/* Sidebar */}
            <div className="lg:col-span-1" data-id="element-498">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24" data-id="element-499">
                <h3 className="font-semibold text-lg mb-4" data-id="element-500">Categories</h3>
                <ul className="space-y-2" data-id="element-501">
                  {faqCategories.map(category => <li key={category.id} data-id="element-502">
                      <a href={`#${category.id}`} className="flex items-center text-gray-700 hover:text-blue-600 py-1" data-id="element-503">
                        <span className="mr-2 text-gray-500" data-id="element-504">
                          {category.icon}
                        </span>
                        {category.title}
                      </a>
                    </li>)}
                </ul>
                <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-505">
                  <h3 className="font-semibold text-lg mb-4" data-id="element-506">
                    Still Need Help?
                  </h3>
                  <a href="/contact" className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-full text-sm font-medium inline-block" data-id="element-507">
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
            {/* FAQ Content */}
            <div className="lg:col-span-3" data-id="element-508">
              {faqCategories.map(category => <div key={category.id} id={category.id} className="mb-10 scroll-mt-24" data-id="element-509">
                  <div className="flex items-center mb-6" data-id="element-510">
                    <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-511">
                      <span className="text-blue-600" data-id="element-512">{category.icon}</span>
                    </div>
                    <h2 className="text-2xl font-bold" data-id="element-513">{category.title}</h2>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-514">
                    <FAQAccordion faqs={category.faqs} data-id="element-515" />
                  </div>
                </div>)}
              {/* Can't find answer */}
              <div className="bg-gray-100 rounded-xl p-8 text-center mt-12" data-id="element-516">
                <h3 className="text-xl font-semibold mb-3" data-id="element-517">
                  Can't find what you're looking for?
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-518">
                  Our customer support team is here to help with any questions
                  you may have.
                </p>
                <div className="flex flex-wrap justify-center gap-4" data-id="element-519">
                  <a href="/contact" className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-full font-medium" data-id="element-520">
                    Contact Us
                  </a>
                  <a href="mailto:support@markethub.com" className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 py-2 px-6 rounded-full font-medium" data-id="element-521">
                    Email Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default FAQPage;